import LandingPageLoyaltyAirportTransfersTile from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyAirportTransfersTile'
import LandingPageLoyaltyDiscountedInsurance from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyDiscountedInsurance'
import LandingPageLoyaltyStayUpgrade from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyStayUpgrade'
import LandingPageLoyaltyLoungePass from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyLoungePass'
import LandingPageLoyaltyPhoneSupport from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyPhoneSupport'
import LandingPageLoyaltyTourExpCredit from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyTourExpCredit'
import LandingPageLoyaltyEsimPackage from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyEsimPackage'
import LandingPageLoyaltyExclusiveSavings from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyExclusiveSavings'
import FormatCurrency from 'components/Common/FormatCurrency/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyLeadRegion from 'loyalty/selectors/getIsLoyaltyLeadRegion'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import { FormattedMessage, useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'

interface Props {
  layout: 'tile' | 'row'
  benefit: App.LoyaltyBenefit
}

function getBenefitLabelDisplay(benefit: App.LoyaltyBenefit, isLeadLoyaltyRegion: boolean, intl: IntlShape) {
  const { unlimited, value, remaining, limit } = benefit
  if (remaining === 0) {
    const redeemedLabel = intl.formatMessage({
      id: 'EPpAGLpGcJglxFBHu5e5',
      defaultMessage: 'Redeemed',
      description: 'Label shown when a loyalty benefit has been fully redeemed',
    })
    return <BodyText variant="small">{redeemedLabel}</BodyText>
  }

  if (
    benefit.code === 'tour_exp_credit' &&
    !isLeadLoyaltyRegion
  ) {
    const voucherLabel = intl.formatMessage(
      {
        id: 'SsGvEuBbnWS634e_SaPx',
        defaultMessage: '{remaining}x {remaining, plural, one {voucher} other {vouchers}}',
        description: 'Label showing remaining voucher count for tour experience credits',
      },
      { remaining: remaining ?? 0 },
    )
    return <BodyText variant="small">{voucherLabel}</BodyText>
  }

  if (benefit.code === 'esim_package' && value?.unit === 'GB') {
    const everyTripLabel = intl.formatMessage({
      defaultMessage: 'For every international trip',
      id: 'akWHJ6sz9uXkT7LQxGR5',
      description: 'Label showing eSIM data is available on every international trip',
    })
    return <BodyText variant="small">{everyTripLabel}</BodyText>
  }

  if (unlimited) {
    const everyTripLabel = intl.formatMessage({
      defaultMessage: 'For every international trip',
      id: 'e56vflFdb6JLFmwgqrAA',
      description: 'Label shown when a loyalty benefit is available on every international trip',
    })
    return <BodyText variant="small">{everyTripLabel}</BodyText>
  }

  if (benefit.code === 'tour_exp_credit' && value?.unit === 'CURRENCY') {
    return (
      <BodyText variant="small">
        <FormattedMessage
          defaultMessage="{remaining}x {amount} credits available"
          id="-f1trF-nOpECOnhR9hzP"
          description="Label showing remaining tour extra credits and their value"
          values={{ remaining, amount: <FormatCurrency value={value.amount} format="dynamic" /> }}
        />
      </BodyText>
    )
  }

  if (value) {
    if (value.unit === 'PERCENTAGE') {
      const discountLabel = remaining === 1
        ? intl.formatMessage(
            {
              id: 'ErpDPWFSvNi_IN0cRDfX',
              defaultMessage: '{amount}% discount',
              description: 'Label showing percentage discount for a single remaining loyalty benefit',
            },
            { amount: value.amount },
          )
        : intl.formatMessage(
            {
              id: 'yJsHW0fE2Bb4_5h5O3JS',
              defaultMessage: '{remaining}x {amount}% discount',
              description: 'Label showing remaining count and percentage discount for loyalty benefit',
            },
            { remaining, amount: value.amount },
          )
      return (
        <BodyText variant="small">
          {discountLabel}
        </BodyText>
      )
    } else if (value.unit === 'CURRENCY') {
      const currencyAmount = <FormatCurrency value={value.amount} format="dynamic" />
      if (remaining === 1) {
        return (
          <BodyText variant="small">
            <FormattedMessage
              id="d6Assk4lCoSVsWjx1S_B"
              defaultMessage="{amount} {remaining, plural, one {voucher} other {vouchers}}"
              description="Value label for one remaining currency-denominated loyalty voucher. amount is a formatted money amount from FormatCurrency, e.g. $100, not the number of vouchers; remaining controls the voucher noun plural. Express voucher worth amount in languages that require it."
              values={{
                remaining: remaining || 0,
                amount: currencyAmount,
              }}
            />
          </BodyText>
        )
      }
      return (
        <BodyText variant="small">
          <FormattedMessage
            id="CrdT-0bch1HgqsbYovuo"
            defaultMessage="{remaining}x {amount} {remaining, plural, one {voucher} other {vouchers}}"
            description="Multiple remaining currency voucher loyalty benefit display with count"
            values={{
              remaining: remaining || 0,
              amount: currencyAmount,
            }}
          />
        </BodyText>
      )
    } else if (value.unit === 'GB') {
      const gbLabel = intl.formatMessage(
        {
          id: 'ILkPHp0mo44MdAMDIEVh',
          defaultMessage: '{amount} GB',
          description: 'eSIM data allowance label for account benefit tile',
        },
        { amount: value.amount },
      )
      return <BodyText variant="small">{gbLabel}</BodyText>
    }
  }

  const availableLabel = intl.formatMessage(
    {
      defaultMessage: '{remaining} of {limit} available',
      id: 'hcS56pQg1e1JAQM4QZUM',
      description: 'Label showing remaining and total available count for a loyalty benefit',
    },
    { remaining, limit: limit ?? remaining },
  )
  return (
    <BodyText variant="small">
      {availableLabel}
    </BodyText>
  )
}

function LoyaltyAccountLandingPageBenefitItem(props: Props) {
  const { layout, benefit } = props
  const intl = useIntl()
  const isLeadLoyaltyRegion = useAppSelector(getIsLoyaltyLeadRegion)
  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const benefitLabel = getBenefitLabelDisplay(benefit, isLeadLoyaltyRegion, intl)

  switch (benefit.code) {
    case 'airport_transfer':
      return <LandingPageLoyaltyAirportTransfersTile key={benefit.code} layout={layout} benefitLabel={benefitLabel} />
    case 'hotel_upgrade':
      return <LandingPageLoyaltyStayUpgrade key={benefit.code} layout={layout} benefitLabel={benefitLabel} />
    case 'lounge_pass':
      return <LandingPageLoyaltyLoungePass key={benefit.code} layout={layout} benefitLabel={benefitLabel} />
    case 'tour_exp_credit':
      return <LandingPageLoyaltyTourExpCredit key={benefit.code} layout={layout} benefitLabel={benefitLabel} />
    case 'discounted_insurance':
      if (!benefit.value) return null
      return <LandingPageLoyaltyDiscountedInsurance key={benefit.code} layout={layout} benefitLabel={benefitLabel} discountPercent={benefit.value.amount} />
    case 'phone_support':
      return <LandingPageLoyaltyPhoneSupport key={benefit.code} layout={layout} />
    case 'family_and_friends_access':
      return <LandingPageLoyaltyExclusiveSavings key={benefit.code} layout={layout} />
    case 'esim_package':
      if (!isEsimEnabled) return null
      return <LandingPageLoyaltyEsimPackage key={benefit.code} layout={layout} benefit={benefit} benefitLabel={benefitLabel} />
    default:
      return null
  }
}

export default LoyaltyAccountLandingPageBenefitItem
